import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { shallow } from "zustand/shallow";
import * as yup from "yup";

import { InputText } from "../../../../common/UI/Inputs";
import AppGetIcon from "../../../../common/UI/AppGetIcon";
import DataTable from "../../../../common/UI/DataTable/dataTable";
import Modal from "../../../../common/UI/Modal";
import AppInputSearch from "../../../../common/UI/Inputs/appInputSearch";
import iconPassView from "../../assets/login/icon-pass-view.svg";
import iconPass from "../../assets/login/icon-pass.svg";
import { useHeaderStore } from "../../../../store/header.store";
import { GetUsersWithPaginationService } from "../../services/admin/getUsersWithPagination.service";
import { GetRolesService } from "../../services/admin/permissionRoles/getRoles.service";

import style from "./Users.module.css";
import styleButton from "../../../../common/theme/styles/button.module.css";
import styleForm from "../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../common/UI/DataTable/dataTable.module.css";
import { CreateOrUpdatedUserCampusService, userData } from "../../services/admin/createOrUpdateUserCampus.service";
import { toast } from "react-toastify";
import { DeleteUserService } from "../../services/admin/deleteUser.service";
import DeleteConfirmationModal from "../../../../common/UI/DataTable/DeleteConfirmationModal/deleteConfirmationModal";
import { useInitDataStore } from "../../../../store/initData.store";

const getUsersWithPaginationService = new GetUsersWithPaginationService();
const createOrUpdatedUserCampusService = new CreateOrUpdatedUserCampusService();
const deleteUserService = new DeleteUserService();

// Schema ajustado a todos los campos del formulario
// 1) Añade `id` al esquema para que yup lo conozca:
const UserSchema = yup.object().shape({
  id: yup
    .number()
    .nullable(), // o string().nullable() si tu id viene como string
  name: yup.string().required("Nombre es requerido"),
  lastName: yup.string().required("Apellido es requerido"),
  email: yup.string().email("Correo inválido").required("Correo es requerido"),
  roleIds: yup.array().min(1, "Debes seleccionar al menos un rol"),
  isActive: yup.boolean(),
  changePassword: yup.boolean(),

  // 2) Password: obrigatório si no hay id (creación) o si hay id y changePassword = true
  password: yup.string().when("id", {
    is: (val: any) => !val, // creación: id nulo/undefined
    then: schema =>
      schema
        .required("Contraseña es requerida")
        .min(6, "La contraseña debe tener al menos 6 caracteres"),
    otherwise: schema =>
      schema.when("changePassword", {
        is: true,
        then: schema =>
          schema
            .required("Contraseña es requerida")
            .min(6, "La contraseña debe tener al menos 6 caracteres"),
        otherwise: schema => schema.notRequired(),
      }),
  }),

  // 3) PasswordConfirmation: misma lógica, además de coincidir con password
  passwordConfirmation: yup.string().when("id", {
    is: (val: any) => !val,
    then: schema =>
      schema
        .required("Confirmar contraseña es requerida")
        .oneOf([yup.ref("password")], "Las contraseñas deben coincidir"),
    otherwise: schema =>
      schema.when("changePassword", {
        is: true,
        then: schema =>
          schema
            .required("Confirmar contraseña es requerida")
            .oneOf([yup.ref("password")], "Las contraseñas deben coincidir"),
        otherwise: schema => schema.notRequired(),
      }),
  }),
});



export default function Users() {
  const headerStore = useHeaderStore(
    (state) => ({
      setModule: state.setModule,
      setModuleItem: state.setModuleItem,
    }),
    shallow
  );

  const [loading, setLoading] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const [rolesData, setRolesData] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHoveredEdit, setIsHoveredEdit] = useState<number | null>(null);
  const [isOpenCurrentDelete, setIsOpenCurrentDelete] = useState<any>(null);
  const [isHoveredDelete, setIsHoveredDelete] = useState<number | null>(null);
  const dataInitDataStore = useInitDataStore(
    (state) => ({
      user: state.initData?.user,
    }),
    shallow
  );
  const [initialData, setInitialData] = useState<userData>({
    id: null,
    name: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    roleIds: [],
    isActive: true,
    changePassword: false, // nuevo campo
  });

  const columns = [
    {
      header: "NOMBRES",
      field: "name",
    },
    {
      header: "APELLIDOS",
      field: "lastName",
    },
    {
      header: "USUARIO",
      field: "email",
    },
    {
      header: "ROLES",
      field: "roles",
      render: (row: any) => {
        // Ajusta aquí si tu propiedad se llama `roleUser` o `rolesUser`
        const userRoles = row.roleUser || row.rolesUser || [];

        // Extraemos sólo el nombre de cada rol
        const names = userRoles.map((ru: any) => ru.role.name);

        // Unimos con comas y devolvemos
        return <div>{names.join(", ")}</div>;
      },
    },
    {
      header: "ESTADO",
      field: "active",
      render: (row: any) => <div>{row.isActive ? "Activo" : "Desactivado"}</div>,
    },
    {
      header: "OPCIONES",
      field: "",
      render: (row: any) => (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          gap: "5px",
        }}>

          <button
            className={styleDataTable.buttom_option}
            onClick={() => handleEdit(row)}
            onMouseEnter={() => setIsHoveredEdit(row.id)}
            onMouseLeave={() => setIsHoveredEdit(null)}
          >
            <AppGetIcon
              name="edit"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredEdit === row.id ? "#FFF" : undefined}
            />
            Editar
          </button>
          <button
            className={styleDataTable.buttom_option}
            onClick={() => {
              setIsOpenCurrentDelete(row);
            }}
            onMouseEnter={(e) => { setIsHoveredDelete(row?.id); }}
            onMouseLeave={(e) => { setIsHoveredDelete(null); }}
          >
            <AppGetIcon
              name="trash"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredDelete === row?.id ? "#FFF" : ""}
            />{" "}
            Eliminar
          </button>
        </div>

      ),
    },
  ];

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteUserService.run(isOpenCurrentDelete?.id,);
      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="checkCircleFill" />
          Operación realizada correctamente
        </div>,
        { icon: false }
      );
      setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación
      setIsOpenCurrentDelete(null); // Cierra el modal
      setLoading(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      toast.error(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
      console.error("Error al eliminar:", error);
      setLoading(false);
    }
  };

  const handleCloseModalDelete = () => {
    // setInitialData(null);
    setIsOpenCurrentDelete(null);
  };


  // 2. En handleEdit, rellena también el id y organizationalJobId
  const handleEdit = (row: any) => {
    const roleIds = (row.rolesUser || row.roleUser || []).map((r: any) => r.roleId);
    const selectedRoles = rolesData.filter(r => roleIds.includes(r.value));

    setInitialData({
      id: row.id,
      name: row.name,
      lastName: row.lastName,
      email: row.email,
      password: '',
      passwordConfirmation: '',
      roleIds: selectedRoles.length ? [selectedRoles[0]] : [], // 👈 solo uno
      isActive: row.isActive,
      changePassword: false,
    });

    setIsEditMode(true);
    setIsOpen(true);
  };



  const handleCloseModal = () => {
    setIsOpen(false);
    setInitialData({
      id: null,
      name: '',
      lastName: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      roleIds: [],
      isActive: true,
      changePassword: false,
    });
    setIsEditMode(false);
  }
  // 3. Llama al servicio en handleSubmit
  const handleSubmit = async (values: typeof initialData) => {
    setLoading(true);

    // decidir si envío password/confirmation
    const isCreating = !isEditMode;
    const shouldSendPassword = isCreating || values.changePassword;

    const payload: any = {
      id: isEditMode ? Number(values.id) : null,
      name: values.name,
      lastName: values.lastName,
      email: values.email,
      roleIds: values.roleIds.map((item: any) => Number(item.value)),
      isActive: values.isActive,
      // envío sólo si aplica según la variable creada
      password: shouldSendPassword ? values.password : undefined,
      passwordConfirmation: shouldSendPassword ? values.passwordConfirmation : undefined,
    };

    try {
      const result = await createOrUpdatedUserCampusService.run(payload, payload.id);
      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {result.message}
        </div>,
        { icon: false }
      );
      setIsReloadData(prev => !prev);
      setIsOpen(false);
      setIsEditMode(false);
      setInitialData({
        id: null,
        name: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        roleIds: [],
        isActive: true,
        changePassword: false,
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Error";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
    } finally {
      setLoading(false);
    }
  };

  const addButton = () => {
    setIsOpen(true);
  };
  useEffect(() => {
    headerStore.setModule("Administración", "");
    headerStore.setModuleItem("Usuarios", "/admin/home");

    (async () => {
      const roles = await GetRolesService();
      setRolesData(roles.map((r: any) => ({ value: r.id, label: r.name })));
    })();
  }, [headerStore]);

  return (
    <div className={style.containerQr}>
      <DataTable
        title="Lista de usuarios"
        columns={columns}
        service={getUsersWithPaginationService}
        reloadData={isReloadData}
        addButton={addButton}
        excelButton={{
          enabled: true,
          nameFile: `Lista de usuarios`,
          getExcelData: (data) => {
            return data.map((row) => {
              // 1) Sacamos el array correcto (roleUser o rolesUser)
              const userRoles = row.roleUser || row.rolesUser || [];
              // 2) Extraemos sólo el nombre de cada rol
              const rolesList = userRoles.map((ru: any) => ru.role.name).join(", ");

              return {
                NOMBRES: row.name ?? "",
                APELLIDOS: row.lastName ?? row.lastnames ?? "",
                USUARIO: row.email,
                ROLES: rolesList,                            // <-- aquí la cadena de roles
                ESTADO: row.isActive ? "Activado" : "Desactivado",
              };
            });
          },
        }}
      >{''}</DataTable>

      <Modal
        title={isEditMode ? "Editar usuario" : "Crear usuario"}
        isOpen={isOpen}
        onClose={handleCloseModal}
        size={{ width: "80%", maxWidth: "1200px", height: "" }}
        loadingModal={loading}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={handleCloseModal}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styleForm.buttomFooter}
              form="userForm"
              disabled={loading}
            >
              Guardar
            </button>
          </div>
        }
      >
        <Formik
          initialValues={initialData}
          validationSchema={UserSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values }) => (

            <Form id="userForm" className="flex-row">
              <div className={styleForm.form__container_row}>

                <InputText classNameInput={styleForm.input__input} type="text" name="name" placeholder="Nombres" TextLabel="Nombres*" />
                <InputText classNameInput={styleForm.input__input} type="text" name="lastName" placeholder="Apellidos" TextLabel="Apellidos*" />
              </div>

              <InputText classNameInput={styleForm.input__input} type="email" name="email" placeholder="Correo" TextLabel="Correo*" />

              <Field name="roleIds">
                {({ field, form }: any) => (
                  <AppInputSearch
                    data={rolesData}
                    classNameInput={styleForm.input__input}
                    selected={Array.isArray(field.value) ? field.value[0] ?? null : field.value ?? null}
                    onSelect={(sel: any) => form.setFieldValue('roleIds', sel ? [sel] : [])}

                    name="roleIds"
                    textLabel="Seleccione rol*"
                  />
                )}
              </Field>
              {initialData.id && (
                <div style={{ padding: '5px 0px' }}>
                  <label className={style.title}>Actualizar contraseña</label>
                  <Field name="changePassword" type="checkbox" />
                </div>
              )}
              <div className={style.form__container_row}>

                <Field name="password">
                  {({ field }: any) => (
                    <InputText
                      {...field}
                      classNameInput={styleForm.input__input}
                      type={'password'}
                      placeholder="Contraseña"
                      TextLabel="Contraseña"
                      disabled={initialData.id && !values.changePassword}
                    />
                  )}
                </Field>

                <Field name="passwordConfirmation">
                  {({ field }: any) => (
                    <InputText
                      {...field}
                      classNameInput={styleForm.input__input}
                      type='password'
                      placeholder="Confirmar contraseña"
                      TextLabel="Confirmar contraseña"
                      disabled={initialData.id && !values.changePassword}

                    />
                  )}
                </Field>
              </div>
              {Number(initialData.id) !== Number(dataInitDataStore.user?.id) && (
                <div style={{ paddingTop: '10px' }}>
                  <label className={style.title}>Activo</label>
                  <Field type="checkbox" name="isActive" />
                </div>
              )}


            </Form>
          )}
        </Formik>
      </Modal>
      {/* ✅ Modal de confirmación de eliminado (nuevo componente) */}
      <DeleteConfirmationModal
        isOpen={Boolean(isOpenCurrentDelete)}
        onClose={handleCloseModalDelete}
        onConfirm={handleDelete}
        loading={loading}
        itemName={isOpenCurrentDelete?.email || isOpenCurrentDelete?.name}
        customMessage="¿Está seguro que desea eliminar este usuario?"
      />

    </div>
  );
}